import { useState } from "react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText } from "@/lib/funnel-planner/i18n";

export function StepNav({
  step,
  totalSteps,
  onNext,
  onBack,
  canGoNext,
  showNext = true,
  content,
  prevStepLabel,
  gamificationAuditTicket,
  gamificationFamily,
  gamificationCurrentState,
  gamificationLiveLabel,
  readinessReadyCount,
  readinessTotalCount,
  readinessTier,
  blockersBucket,
  supportedStates,
}: {
  step: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  canGoNext: boolean;
  showNext?: boolean;
  content: FunnelPlannerContent;
  prevStepLabel?: string;
  gamificationAuditTicket?: string;
  gamificationFamily?: string;
  gamificationCurrentState?: string;
  gamificationLiveLabel?: string;
  readinessReadyCount?: number;
  readinessTotalCount?: number;
  readinessTier?: string;
  blockersBucket?: string;
  supportedStates?: string;
}) {
  const [backActive, setBackActive] = useState(false);
  const [nextActive, setNextActive] = useState(false);

  return (
    <div
      className="funnel-planner-step-nav sticky z-20 mt-3 rounded-md border border-slate-700/70 px-3 py-2 shadow-xl"
      data-funnel-planner-step-nav
      data-audit-ticket={gamificationAuditTicket}
      data-tier-one-family={gamificationFamily}
      data-gamification-current-state={gamificationCurrentState}
      data-gamification-reduced-motion-static={gamificationAuditTicket ? "supported" : undefined}
      data-gamification-supported-states={supportedStates}
      data-readiness-ready-count={readinessReadyCount}
      data-readiness-total-count={readinessTotalCount}
      data-readiness-tier={readinessTier}
      data-blockers-bucket={blockersBucket}
      style={{
        position: "sticky",
        top: "4.75rem",
        background: "linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(8, 13, 25, 0.94))",
        backdropFilter: "blur(14px)",
      }}
    >
      {gamificationLiveLabel ? (
        <span className="sr-only" role="status" aria-live="polite">
          {gamificationLiveLabel}
        </span>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <span className="sr-only">
          {funnelText(content, "stepProgressLabel", "Step {step} of {total} - about 3 minutes", {
            step,
            total: totalSteps,
          })}
        </span>
        {step > 1 ? (
          <button
            onClick={onBack}
            onMouseDown={() => setBackActive(true)}
            onMouseUp={() => setBackActive(false)}
            onMouseLeave={() => setBackActive(false)}
            style={{
              backgroundColor: backActive ? "#334155" : "transparent",
            }}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={prevStepLabel
              ? funnelText(content, "previousStepAriaLabel", "Go to previous step") + `: ${prevStepLabel}`
              : funnelText(content, "previousStepAriaLabel", "Go to previous step")}
          >
            {content.backButton}
          </button>
        ) : (
          <div aria-hidden="true" />
        )}

        <span
          aria-hidden="true"
          style={{
            fontSize: 12,
            color: "#94a3b8",
            whiteSpace: "nowrap",
          }}
        >
          {step} / {totalSteps}
        </span>

        {showNext && (
          <button
            onClick={onNext}
            disabled={!canGoNext}
            onMouseDown={() => setNextActive(true)}
            onMouseUp={() => setNextActive(false)}
            onMouseLeave={() => setNextActive(false)}
            style={{
              backgroundColor: nextActive && canGoNext ? "rgba(37,99,235,0.80)" : undefined,
              transform: nextActive && canGoNext ? "scale(0.98)" : undefined,
            }}
            className="rounded-md bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={funnelText(content, "nextStepAriaLabel", "Go to next step")}
          >
            {content.nextButton}
          </button>
        )}
      </div>
    </div>
  );
}
